import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  RESOURCE_GRANTING_GROUPS_QUERY,
  USER_GROUPS_QUERY,
  SET_RESOURCE_SECURITY_GROUPS,
} from '@scio3/graphql-client';
import {
  Button,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@scio3/ui';
import { toast } from 'sonner';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';
import { MultiSelectCombobox, type ComboboxItem } from '@/components/shared/MultiSelectCombobox';
import type { UserGroupsQueryResult, UserGroupResourceType } from '../types';

/**
 * Manage Group Security for a single resource (Model B) — the reverse entry point: from a folder/item,
 * choose which user groups it's shared to. Grants live on the object's `securityGroupIds`; reads the
 * current groups via resourceGrantingGroups and writes the whole set via setResourceSecurityGroups.
 * Empty selection ⇒ shared to nobody. Groups are account-wide (visibility/team scoping deprecated),
 * so every group is pickable regardless of team.
 */
interface Props {
  resourceType: UserGroupResourceType; // 'GROUP' (folder) | 'DEVICE' | 'ASSET' | 'PLAYLIST' | 'SCHEDULE'
  resourceId: string;
  /** @deprecated Vestigial — groups are account-wide; no longer used to scope the picker. */
  teamId?: string;
  resourceName?: string;
  onClose: () => void;
  onSaved?: () => void;
}

interface GrantingGroupsResult {
  resourceGrantingGroups: { _id: string; name?: string }[];
}

export function ManageResourceGroupsDialog({
  resourceType,
  resourceId,
  resourceName,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();

  const { data: grantingData, loading: loadingGranting } = useQuery<GrantingGroupsResult>(
    RESOURCE_GRANTING_GROUPS_QUERY,
    {
      variables: { resourceType, resourceId },
      skip: !resourceId,
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: groupsData } = useQuery<UserGroupsQueryResult>(USER_GROUPS_QUERY, {
    // Groups are account-wide (visibility/team scoping deprecated) — list them all.
    variables: { query: {} },
    fetchPolicy: 'cache-and-network',
  });

  const allGroups: ComboboxItem[] = (groupsData?.userGroups ?? []).map((g) => ({
    id: g._id,
    label: g.name ?? g._id,
  }));
  const currentGroupIds = new Set((grantingData?.resourceGrantingGroups ?? []).map((g) => g._id));

  // Selection defaults to the current granting groups (once loaded). Local, initialized lazily.
  const [selected, setSelected] = useState<ComboboxItem[] | null>(null);
  const [search, setSearch] = useState('');
  const effectiveSelected: ComboboxItem[] =
    selected ??
    (grantingData?.resourceGrantingGroups ?? []).map((g) => ({
      id: g._id,
      label: g.name ?? g._id,
    }));

  const [setGroups, { loading: applying }] = useMutation(SET_RESOURCE_SECURITY_GROUPS, {
    refetchQueries: [
      'ResourceGrantingGroups',
      'DeviceGroups',
      'AssetGroups',
      'PlaylistGroups',
      'ScheduleGroups',
    ],
  });

  const selectedIds = new Set(effectiveSelected.map((s) => s.id));
  const isDirty =
    [...selectedIds].some((id) => !currentGroupIds.has(id)) ||
    [...currentGroupIds].some((id) => !selectedIds.has(id));

  const { disabled, disabledTooltip } = useDialogSubmitState({ isSubmitting: applying, isDirty });

  const handleSave = async () => {
    try {
      // Model B: set the object's securityGroupIds to exactly the selected groups (empty ⇒ unshared).
      await setGroups({ variables: { resourceType, resourceId, groupIds: [...selectedIds] } });
      toast.success(t('Group access updated'));
      onSaved?.();
      onClose();
    } catch (err) {
      const raw = err instanceof Error ? err.message : '';
      if (raw.includes('FORBIDDEN_RESOURCE_GRANT'))
        toast.error(t('You don’t have permission to change sharing for this item'));
      else if (raw.includes('INVALID_GROUP_ID'))
        toast.error(t('One of the selected groups no longer exists'));
      else toast.error(`${t('Failed to update group access')}: ${raw}`);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="manage-resource-groups-dialog">
        <DialogHeader>
          <DialogTitle>
            {resourceName
              ? t('Group Security — {{name}}', { name: resourceName })
              : t('Group Security')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            {t(
              'Choose which groups can reach this item. Members of a selected group get the group’s role here.',
            )}
          </p>
          <div className="space-y-1.5">
            <Label>{t('Groups')}</Label>
            <MultiSelectCombobox
              items={allGroups}
              selectedItems={effectiveSelected}
              onToggle={(item) =>
                setSelected((prev) => {
                  const base = prev ?? effectiveSelected;
                  return base.some((p) => p.id === item.id)
                    ? base.filter((p) => p.id !== item.id)
                    : [...base, item];
                })
              }
              searchQuery={search}
              onSearchChange={setSearch}
              loading={loadingGranting}
              placeholder={t('Select groups')}
              searchPlaceholder={t('Search groups')}
              emptyMessage={t('No groups')}
              data-testid="resource-groups-select"
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" onClick={onClose}>
              {t('Cancel')}
            </Button>
          </DialogClose>
          <Button
            onClick={handleSave}
            disabled={disabled}
            title={disabledTooltip}
            data-testid="resource-groups-save"
          >
            {applying ? t('Saving…') : t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
